/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import type { ComponentType } from 'react';

import {
  TYPE_REGISTRE_BY_TYPE_ACTE,
  TYPE_REGISTRE_MARIAGE,
  URL_ACTE_INDIVIDUS,
  URL_NUMERISATION_DECES,
  URL_NUMERISATION_MARIAGE,
  URL_NUMERISATION_NAISSANCE,
  URL_NUMERISATION_RECONNAISSANCE,
  URL_SOUS_TYPES_ACTE_DECES,
  URL_SOUS_TYPES_ACTE_MARIAGE,
  URL_TYPES_RECONNAISSANCE,
  regleEtatAnterieurConjoint,
  regleEtatMatrimonial,
} from '@/constants';
import type { CleMentionActe, TypeActe } from '@/types';
import { libellesParentsAvecArticle, libellesParentsOnglet } from '@/constants';
import {
  civiliteAffichee,
  formatCommunePays,
  formatDateHeure,
  formatDateSeule,
  formatDepartement,
  formatRegistreActe,
  sexeDepuisLettre,
  nomAffiche,
  prenomsAffiches,
} from '@/utils';
import { ActeNaissanceListeContainer } from '../ListeNaissance';
import { ActeReconnaissanceListeContainer } from '../ListeReconnaissance';
import { ActeDecesListeContainer } from '../ListeDeces';
import { ActeMariageListeContainer } from '../ListeMariage';

import type { SectionChamps, PersonneTab } from './Blocs';
import ModalDeclarant from './Modales/ModalDeclarant';
import ModalDefunt from './Modales/ModalDefunt';
import ModalDefuntDivers from './Modales/ModalDefuntDivers';
import { ModalConjoint1, ModalConjoint2 } from './Modales/ModalConjoint';
import {
  ModalConjoint1Divers,
  ModalConjoint2Divers,
} from './Modales/ModalConjointDivers';
import ModalContratMariage from './Modales/ModalContratMariage';
import {
  ModalEtatMatrimonialParents1,
  ModalEtatMatrimonialParents2,
} from './Modales/ModalEtatMatrimonialParents';
import ModalEvenementMariage from './Modales/ModalEvenementMariage';
import ModalEnfant from './Modales/ModalEnfant';
import ModalEvenementDeces from './Modales/ModalEvenementDeces';
import ModalEvenementNaissance from './Modales/ModalEvenementNaissance';
import ModalEvenementReconnaissance from './Modales/ModalEvenementReconnaissance';
import ModalParent from './Modales/ModalParent';
import ModalRegistreActe from './Modales/ModalRegistreActe';

/** Dossier complet renvoyé par `GET /numerisation-actes/{type}/{id}`. */
export interface DossierActe {
  acte?: any;
  /** Individu principal d'une naissance / d'une reconnaissance. */
  enfant?: any;
  /** Individu principal d'un décès. */
  defunt?: any;
  parent1?: any;
  parent2?: any;
  /** Propre au décès. */
  declarant?: any;
  /* Propres au MARIAGE : deux conjoints, quatre parents, un notaire. */
  conjoint1?: any;
  conjoint2?: any;
  parent1Conjoint1?: any;
  parent2Conjoint1?: any;
  parent1Conjoint2?: any;
  parent2Conjoint2?: any;
  notaire?: any;
}

export interface FicheConfig {
  /** Base d'URL passée à `useFiche`, qui y concatène `/{identifiant}`. */
  url: string;
  /**
   * Endpoint générique des individus d'acte (`PUT {urlIndividus}/{id}`).
   *
   * Utilisé par tous les types d'acte : éditer un individu ne dépend pas du PUT
   * du dossier, et `ActeIndividuRepository::update` alimente lui aussi le
   * référentiel des professions (cf. `AlimenteReferentielProfession`). Laisser
   * ce champ vide ferait retomber les modales sur `service.update`.
   */
  urlIndividus?: string;
  /** En-tête de la fiche, ex. « Acte de naissance de DUPONT Léa ». */
  getTitre: (dossier: DossierActe) => string;
  /** Titre du panneau de l'évènement — sert aussi d'ancre dans le menu. */
  titrePanelEvenement: string;
  /**
   * Titre d'un QUATRIÈME panneau, propre au type d'acte, rendu après
   * l'évènement — et son ancre dans le menu.
   *
   * Absent (naissance, reconnaissance, décès), la fiche n'en a que trois. Le
   * mariage l'utilise pour son contrat, que la maquette range à part de la
   * célébration, avec son propre crayon.
   */
  titrePanelComplement?: string;
  /**
   * Clé de l'individu que la modale du panneau complémentaire édite, quand elle
   * en édite un. Pour le mariage : le notaire du contrat.
   */
  cleIndividuComplement?: string;
  /** Clé de rattachement des mentions côté API. */
  cleMentionActe: CleMentionActe;
  /** Onglets du panneau INDIVIDUS. */
  onglets: Array<{ key: PersonneTab; label: string }>;
  /**
   * Sidebar gauche de la fiche : la liste des actes de ce type, avec
   * recherche et navigation vers leur propre fiche au clic — même montage que
   * la sidebar de `RecenseFiche` (recensement-citoyen), qui pousse la liste
   * complète des recensés.
   */
  ListeSidebar: ComponentType<{ sidebar?: boolean }>;
  /**
   * Titre du panneau des individus, selon l'onglet actif. Absent, le panneau
   * garde « INDIVIDUS » — comportement de la naissance et de la reconnaissance.
   * Le décès le fait varier avec le rôle, comme sa maquette.
   */
  getTitrePanelIndividus?: (tab: PersonneTab) => string;
  /** Descripteurs des blocs : fonctions pures dossier → données affichables. */
  blocs: {
    registreActe: (dossier: DossierActe) => SectionChamps[];
    evenement: (dossier: DossierActe) => SectionChamps[];
    individu: (
      individu: any,
      tab: PersonneTab,
      dossier: DossierActe,
    ) => SectionChamps[];
    /** Descripteur du panneau complémentaire ; absent, le panneau n'existe pas. */
    complement?: (dossier: DossierActe) => SectionChamps[];
  };
  /**
   * Modales d'édition.
   *
   * ⚠️ Une modale absente rend son bloc en LECTURE SEULE — c'est le seul
   * mécanisme du mode consultation, il n'y a pas de booléen `editable`. Un
   * crayon ne peut donc pas appeler un endpoint inexistant, mais n'en déclarer
   * une que si son enregistrement a une cible réelle.
   */
  /**
   * Props additionnelles passées à `ModalRegistreActe` : le référentiel du
   * sous-type et les types de registre diffèrent d'un acte à l'autre.
   */
  propsModaleRegistre?: Record<string, unknown>;
  /**
   * Props additionnelles passées à la modale d'un parent : la profession n'est
   * saisie qu'en naissance.
   */
  propsModaleParent?: Record<string, unknown>;
  /**
   * `true` si l'évènement est porté par l'ACTE (reconnaissance) et non par
   * l'enfant (naissance) : détermine quelle partie du dossier l'enregistrement
   * doit rafraîchir.
   */
  evenementPorteParLActe?: boolean;
  modales?: {
    registreActe?: ComponentType<any>;
    evenement?: ComponentType<any>;
    /**
     * Modales des onglets NON parent, indexées par onglet : un type d'acte peut
     * porter plusieurs individus principaux aux contrats distincts — le défunt
     * et le déclarant n'ont ni les mêmes champs ni la même validation.
     */
    individus?: Partial<Record<PersonneTab, ComponentType<any>>>;
    /**
     * Modale des onglets PARENT (`parent1`/`parent2`), au contrat de props
     * distinct (`numeroParent`, `parent`, `naissanceDateEnfant`). C'est cette
     * différence de contrat, et non le rôle, qui justifie une entrée à part.
     */
    parent?: ComponentType<any>;
    /** Modale du panneau complémentaire ; son absence le rend en lecture seule. */
    complement?: ComponentType<any>;
  };
}

/** Les 4 derniers champs du bloc registre sont communs à tous les types d'acte. */
const champsActe = (acte?: any) => [
  {
    libelle: 'Dressé le',
    valeur: formatDateHeure(
      acte?.dateDresseActe,
      acte?.heureDresseActe,
      acte?.minuteDresseActe,
    ),
    computer: 3,
    tablet: 8,
    mobile: 16,
  },
  {
    libelle: 'Acte N°',
    valeur: acte?.numeroActe,
    computer: 2,
    tablet: 8,
    mobile: 16,
  },
  {
    libelle: 'Ordre N°',
    valeur: acte?.numeroOrdre,
    computer: 2,
    tablet: 8,
    mobile: 16,
  },
  {
    libelle: 'Complément n°',
    valeur: acte?.acteNumeroComplement ?? '-',
    computer: 2,
    tablet: 8,
    mobile: 16,
  },
];

/** Civilité / Nom / Prénoms, communs aux 4 cas du panneau INDIVIDUS. */
const champsEtatCivil = (individu?: any, largeurs = [3, 4, 5]) => [
  {
    libelle: 'Civilité',
    valeur: civiliteAffichee(individu),
    computer: largeurs[0],
    tablet: 8,
    mobile: 16,
  },
  {
    libelle: "Nom (nom d'usage)",
    valeur: nomAffiche(individu),
    computer: largeurs[1],
    tablet: 8,
    mobile: 16,
  },
  {
    libelle: 'Prénoms',
    valeur: prenomsAffiches(individu),
    computer: largeurs[2],
    tablet: 8,
    mobile: 16,
  },
];

/**
 * Département + Commune (Pays) de naissance d'un individu.
 *
 * `largeurs` par défaut `[4, 8]` : la ligne occupe alors 12 colonnes, ce qu'elle
 * fait dans les sections d'individu où elle est SEULE sur sa ligne.
 *
 * ⚠️ À passer à `[3, 9]` quand la ligne commence par une date, pour que la
 * commune tombe sous le « Dressé le » du panneau de l'acte — cf.
 * `LARGEURS_LIEU_APRES_DATE`.
 */
const ligneLieuNaissance = (
  individu?: any,
  largeurs: [number, number] = [4, 8],
) => ({
  champs: [
    {
      libelle: 'Département',
      valeur: formatDepartement(
        individu?.naissanceDepartementCode,
        individu?.naissanceDepartementLibelle,
      ),
      computer: largeurs[0],
      tablet: 8,
      mobile: 16,
    },
    {
      libelle: 'Commune (Pays)',
      valeur: formatCommunePays(
        individu?.naissanceCodePostal,
        individu?.naissanceLocalite,
        individu?.naissancePays,
      ),
      computer: largeurs[1],
      tablet: 16,
      mobile: 16,
    },
  ],
});

/**
 * Largeurs du lieu quand il SUIT une date sur la ligne d'un panneau
 * d'évènement — naissance, décès, mariage.
 *
 * ⚠️ Ce n'est pas un réglage esthétique. Le panneau de l'acte, juste au-dessus,
 * pose « Registre » sur 4 puis « Sous-type d'acte » sur 3 : son « Dressé le »
 * commence donc en colonne 8. Un lieu en `[4, 8]` après une date de 4 place la
 * commune en colonne 9 — décalée d'une seule colonne, ce qui se voit et se lit
 * mal. En `[3, 9]`, le département s'aligne sur le sous-type et la commune sur
 * le « Dressé le ».
 */
const LARGEURS_LIEU_APRES_DATE: [number, number] = [3, 9];

/**
 * Département + Commune (Pays) d'un bloc de lieu QUELCONQUE d'un individu
 * (`deces*`, `domicile*`), symétrique de `ligneLieuNaissance`.
 */
const ligneLieuPrefixe = (
  individu: any,
  prefixe: string,
  /**
   * Largeurs du département et de la commune (défaut `[4, 8]`, comportement
   * historique).
   *
   * À resserrer quand le lieu PARTAGE sa ligne avec d'autres champs : le total
   * d'une ligne doit tenir en 16, sinon la grille renvoie le dernier champ à la
   * ligne suivante — c'est ce qui coupait le panneau du contrat de mariage en
   * deux.
   */
  largeurs: [number, number] = [4, 8],
) => ({
  champs: [
    {
      libelle: 'Département',
      valeur: formatDepartement(
        individu?.[`${prefixe}DepartementCode`],
        individu?.[`${prefixe}DepartementLibelle`],
      ),
      computer: largeurs[0],
      tablet: 8,
      mobile: 16,
    },
    {
      libelle: 'Commune (Pays)',
      valeur: formatCommunePays(
        individu?.[`${prefixe}CodePostal`],
        individu?.[`${prefixe}Localite`],
        individu?.[`${prefixe}Pays`],
      ),
      computer: largeurs[1],
      tablet: 16,
      mobile: 16,
    },
  ],
});

/**
 * Section « ETAT MATRIMONIAL » de l'onglet du défunt.
 *
 * ⚠️ Seul descripteur d'individu du registre à lire l'ACTE plutôt que l'individu
 * reçu : ses colonnes sont sur `ec_actd`, mais c'est du défunt qu'il s'agit.
 *
 * Les champs affichés dépendent de la situation (cf.
 * `constants/etatMatrimonialDeces`) : célibataire et union libre n'en ont aucun,
 * seules trois portent une date, et le Pacs affiche à la place la situation qui
 * le précédait. Montrer une case vide n'informe pas, elle fait douter.
 */
const sectionEtatMatrimonial = (acte?: any): SectionChamps => {
  const regle = regleEtatMatrimonial(acte?.etatMatrimonialCode);

  const situation = {
    libelle: 'Etat matrimonial',
    valeur: acte?.defuntEtatMatrimonialLibelle,
    computer: 4,
    tablet: 8,
    mobile: 16,
  };

  const premiereLigne = [
    situation,
    ...(regle.libelleDate
      ? [
          {
            libelle: regle.libelleDate,
            valeur: formatDateSeule(acte?.dateEtatMatrimonial),
            computer: 4,
            tablet: 8,
            mobile: 16,
          },
        ]
      : []),
    ...(regle.etatAnterieurPacs
      ? [
          {
            libelle: 'Situation avant le Pacs',
            valeur: acte?.defuntEtatMatrimonialLibellePacs,
            computer: 8,
            tablet: 16,
            mobile: 16,
          },
        ]
      : []),
  ];

  // « ex-partenaire » sur un Pacs, « ex-conjoint » ailleurs.
  const role =
    regle.roleConjoint === 'partenaire' ? 'ex-partenaire' : 'ex-conjoint';

  return {
    titre: 'ETAT MATRIMONIAL',
    separateur: false,
    lignes: [
      { champs: premiereLigne },
      ...(regle.roleConjoint
        ? [
            {
              champs: [
                {
                  libelle: `Sexe ${role}`,
                  valeur: sexeDepuisLettre(acte?.defuntSexeExConjoint),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: "Nom (nom d'usage)",
                  valeur: acte?.defuntNomExConjoint,
                  computer: 3,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Prénoms',
                  valeur: acte?.defuntPrenomExConjoint,
                  computer: 9,
                  tablet: 16,
                  mobile: 16,
                },
              ],
            },
          ]
        : []),
    ],
  };
};

/**
 * Section « DIVERS » : la profession.
 *
 * Le titre est le même partout depuis l'harmonisation de la fiche — la naissance
 * disait « AUTRE INFORMATION », ce qui ne nommait rien.
 */
const sectionProfession = (
  individu?: any,
  // Défaut inchangé pour la naissance ; la maquette du décès dit « DIVERS ».
  titre = 'DIVERS',
  /**
   * Trait au-dessus (défaut `true`, comportement naissance). Passé à `false`
   * quand la section partage son crayon avec celle du dessus : un trait entre
   * deux sections d'un même groupe suggérerait qu'elles s'éditent séparément.
   */
  separateur = true,
  /** Modale propre à cette section, qui ouvre alors un nouveau groupe. */
  modale?: SectionChamps['modale'],
): SectionChamps => ({
  titre,
  separateur,
  ...(modale && { modale }),
  lignes: [
    {
      champs: [
        {
          libelle: 'Profession',
          valeur: individu?.profession,
          computer: 6,
          tablet: 16,
          mobile: 16,
        },
      ],
    },
  ],
});

/**
 * Heure/minute de la reconnaissance.
 *
 * L'API expose un champ unifié (« père en priorité, sinon mère », comme pour
 * `dateReconnaissance`) ; le repli sur les champs ventilés permet d'afficher
 * correctement la fiche même avant que cette exposition ne soit livrée.
 */
const heureReconnaissance = (acte?: any) =>
  acte?.heureReconnaissance ??
  acte?.reconnaissancePereHeure ??
  acte?.reconnaissanceMereHeure;

const minuteReconnaissance = (acte?: any) =>
  acte?.minuteReconnaissance ??
  acte?.reconnaissancePereMinute ??
  acte?.reconnaissanceMereMinute;

const ONGLETS_ENFANT_PARENTS: Array<{ key: PersonneTab; label: string }> = [
  { key: 'enfant', label: 'ENFANT' },
  { key: 'parent1', label: 'PARENT 1' },
  { key: 'parent2', label: 'PARENT 2' },
];

/**
 * Onglets d'un type d'acte dont les parents ont un rôle imposé : les clés
 * restent `parent1` / `parent2` (elles indexent le dossier renvoyé par l'API),
 * seuls les libellés portent le rôle.
 */
const ongletsAvecRoles = (
  typeActe: TypeActe,
): Array<{ key: PersonneTab; label: string }> => {
  const libelles = libellesParentsOnglet(typeActe);

  return libelles
    ? [
        { key: 'enfant', label: 'ENFANT' },
        { key: 'parent1', label: libelles[1] },
        { key: 'parent2', label: libelles[2] },
      ]
    : ONGLETS_ENFANT_PARENTS;
};

/**
 * Onglets du décès. Les clés indexent le dossier renvoyé par l'API.
 *
 * Les parents gardent « PARENT 1 / PARENT 2 », comme la naissance — décision
 * produit, cf. `constants/rolesParentsParTypeActe` où le décès n'a pas d'entrée.
 */
/**
 * Section « ETAT MATRIMONIAL ANTÉRIEUR » de l'onglet d'un conjoint.
 *
 * ⚠️ Lit l'ACTE et non l'individu reçu : ses colonnes sont sur `ec_actm`, mais
 * c'est du conjoint qu'il s'agit — même répartition que la situation
 * matrimoniale du défunt.
 *
 * Les champs affichés dépendent de la situation (cf.
 * `constants/etatMatrimonialMariage`) : célibataire n'en a aucun, les trois
 * autres portent une date au libellé propre et l'identité de l'ancien conjoint.
 * Montrer une case vide n'informe pas, elle fait douter.
 */
const sectionEtatAnterieur = (
  acte: any,
  numeroConjoint: 1 | 2,
): SectionChamps => {
  const prefixe = `conjoint${numeroConjoint}`;
  const regle = regleEtatAnterieurConjoint(
    acte?.[`${prefixe}EtatMatrimonialAnterieureCode`],
  );

  const role =
    regle.roleExConjoint === 'partenaire' ? 'partenaire' : 'conjoint';

  return {
    titre: 'ETAT MATRIMONIAL ANTÉRIEUR',
    separateur: false,
    lignes: [
      {
        champs: [
          {
            libelle: 'Etat matrimonial antérieur',
            valeur: acte?.[`${prefixe}EtatMatrimonialAnterieureLibelle`],
            computer: 4,
            tablet: 8,
            mobile: 16,
          },
          ...(regle.libelleDate
            ? [
                {
                  libelle: regle.libelleDate,
                  valeur: formatDateSeule(
                    acte?.[`${prefixe}DateEtatMatrimonial`],
                  ),
                  computer: 3,
                  tablet: 8,
                  mobile: 16,
                },
              ]
            : []),
          ...(regle.roleExConjoint
            ? [
                {
                  libelle: `Nom ex-${role}`,
                  valeur: acte?.[`${prefixe}NomExConjoint`],
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: `Prénoms ex-${role}`,
                  valeur: acte?.[`${prefixe}PrenomExConjoint`],
                  computer: 5,
                  tablet: 8,
                  mobile: 16,
                },
              ]
            : []),
        ],
      },
    ],
  };
};

/**
 * Sections d'un PARENT de mariage, au sein d'un onglet qui en porte deux.
 *
 * ⚠️ `cleIndividu` est ce qui rend l'onglet à deux individus possible : elle dit
 * de qui la section parle, et ouvre par là même un groupe — donc un crayon et
 * une modale propres. `numeroParent` donne son rang à `ModalParent`, que
 * l'onglet ne peut plus déduire de son nom.
 */
const sectionsParentMariage = (
  individu: any,
  cleIndividu: string,
  numeroParent: 1 | 2,
): SectionChamps[] => [
  {
    titre: `INFORMATIONS SUR LE PARENT ${numeroParent}`,
    // Trait entre les deux parents, qui sont deux blocs éditables distincts.
    separateur: true,
    cleIndividu,
    numeroParent,
    lignes: [
      {
        champs: [
          {
            libelle: 'Né le',
            valeur: formatDateSeule(individu?.naissanceDate),
            computer: 4,
            tablet: 8,
            mobile: 16,
          },
          ...champsEtatCivil(individu),
        ],
      },
      ligneLieuNaissance(individu),
      {
        champs: [
          {
            libelle: 'Profession',
            valeur: individu?.profession,
            computer: 6,
            tablet: 16,
            mobile: 16,
          },
        ],
      },
    ],
  },
];

/**
 * Onglets du mariage.
 *
 * ⚠️ `parentsConjoint1` / `parentsConjoint2` ne désignent PAS un individu du
 * dossier : chacun en montre deux, et chaque section dit lequel elle décrit.
 * C'est le seul type d'acte dans ce cas.
 *
 * ⚠️ Le NOTAIRE n'a pas d'onglet : la maquette le range dans le panneau du
 * contrat de mariage, avec la date de sa rédaction — c'est du contrat qu'il
 * s'agit, pas d'une personne de l'acte au même titre que les conjoints.
 */
/**
 * Section « ETAT MATRIMONIAL DES PARENTS », en tête de l'onglet des parents d'un
 * conjoint.
 *
 * ⚠️ UNE situation pour DEUX personnes : elle qualifie le couple, et `ec_actm` ne
 * la porte qu'une fois par conjoint (`am_pmex_ematc`). Elle n'appartient donc ni
 * au bloc du parent 1 ni à celui du parent 2 — d'où son crayon propre, et sa
 * place EN TÊTE de l'onglet : on lit la situation du couple avant les deux
 * personnes qu'elle qualifie. C'est aussi ainsi que l'assistant la présente.
 *
 * ⚠️ Lit l'ACTE et non un individu, comme la situation antérieure d'un conjoint.
 * Déclarer sa `modale` la sort du contrat de `ModalParent`
 * (cf. `GroupeSections.modaleDeSection`).
 */
const sectionEtatMatrimonialParents = (
  acte: any,
  numeroConjoint: 1 | 2,
): SectionChamps => ({
  titre: 'ETAT MATRIMONIAL DES PARENTS',
  separateur: true,
  modale:
    numeroConjoint === 1
      ? ModalEtatMatrimonialParents1
      : ModalEtatMatrimonialParents2,
  lignes: [
    {
      champs: [
        {
          libelle: 'Etat matrimonial des parents',
          valeur: acte?.[`parentsConj${numeroConjoint}EtatMatrimonialLibelle`],
          computer: 6,
          tablet: 16,
          mobile: 16,
        },
      ],
    },
  ],
});

const ONGLETS_MARIAGE: Array<{ key: PersonneTab; label: string }> = [
  { key: 'conjoint1', label: 'CONJOINT 1' },
  { key: 'parentsConjoint1', label: 'PARENTS DU CONJOINT 1' },
  { key: 'conjoint2', label: 'CONJOINT 2' },
  { key: 'parentsConjoint2', label: 'PARENTS DU CONJOINT 2' },
];

/**
 * Panneau « CONTRAT DE MARIAGE » : son existence, sa date, et le notaire qui l'a
 * reçu.
 *
 * ⚠️ Un panneau à part, et non une section du bloc de la célébration : la
 * maquette lui donne son propre crayon, et sa modale écrit sur deux tables
 * (l'acte et l'individu notaire).
 *
 * La signature et sa date tiennent en UN champ, comme la maquette
 * (« Oui — contrat rédigé le 02/03/2023 ») : sans contrat la date n'a rien à
 * dire, l'afficher vide ferait douter.
 *
 * ⚠️ Quatre champs de largeur 4 : le total fait exactement 16, donc une seule
 * ligne, et « Notaire » tombe sous le « Département » du panneau de la
 * célébration, dont le premier champ occupe lui aussi 4. Les largeurs héritées
 * du lieu (4 puis 8) portaient la ligne à 20 et renvoyaient la commune en
 * dessous.
 */
const sectionContratMariage = (acte: any, notaire: any): SectionChamps[] => {
  const existe = Boolean(acte?.contratMariageExiste);
  const dateRedaction = formatDateSeule(acte?.contratMariageDate);

  return [
    {
      lignes: [
        {
          champs: [
            {
              libelle: "Signature d'un contrat",
              valeur: existe
                ? ['Oui', dateRedaction && `contrat rédigé le ${dateRedaction}`]
                    .filter(Boolean)
                    .join(' — ')
                : 'Non',
              computer: 4,
              tablet: 16,
              mobile: 16,
            },
            ...(existe
              ? [
                  {
                    libelle: 'Notaire',
                    valeur: [notaire?.nomFamille, notaire?.prenom]
                      .filter(Boolean)
                      .join(' '),
                    computer: 3,
                    tablet: 8,
                    mobile: 16,
                  },
                  ...ligneLieuPrefixe(notaire, 'domicile', [3, 4]).champs,
                ]
              : []),
          ],
        },
      ],
    },
  ];
};

const ONGLETS_DECES: Array<{ key: PersonneTab; label: string }> = [
  { key: 'defunt', label: 'DÉFUNT' },
  { key: 'parent1', label: 'PARENT 1' },
  { key: 'parent2', label: 'PARENT 2' },
  { key: 'declarant', label: 'DÉCLARANT' },
];

/**
 * Registre `TypeActe → configuration de la fiche`, sur le modèle
 * d'`ASSISTANT_CONFIG` (`components/Assistant/assistantConfig.tsx`).
 *
 * Colocalisé dans `components/Acte/Fiche` (et non `src/constants`) pour éviter
 * un cycle d'imports `constants → components → constants` : le registre
 * référence des composants de modales.
 *
 * Ajouter le mariage ou le décès doit se limiter à ajouter une entrée ici.
 */
const FICHE_CONFIG: Partial<Record<TypeActe, FicheConfig>> = {
  naissance: {
    url: URL_NUMERISATION_NAISSANCE,
    urlIndividus: URL_ACTE_INDIVIDUS,
    getTitre: ({ acte }) =>
      `Acte de naissance de ${acte?.nomClassementEnfant ?? ''}`,
    titrePanelEvenement: 'INFORMATIONS SUR LA NAISSANCE',
    cleMentionActe: 'identifiantActeNaissance',
    onglets: ONGLETS_ENFANT_PARENTS,
    ListeSidebar: ActeNaissanceListeContainer,
    blocs: {
      registreActe: ({ acte }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Registre',
                  valeur: formatRegistreActe(acte),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: "Sous-type d'acte",
                  valeur: acte?.libelleTypeActe,
                  computer: 3,
                  tablet: 8,
                  mobile: 16,
                },
                ...champsActe(acte),
              ],
            },
          ],
        },
      ],
      // La naissance décrit l'évènement depuis l'ENFANT (champs plats
      // `naissance*`), là où la reconnaissance le décrira depuis l'acte.
      evenement: ({ enfant }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Né le',
                  valeur: formatDateHeure(
                    enfant?.naissanceDate,
                    enfant?.naissanceHeure,
                    enfant?.naissanceMinute,
                  ),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                ...ligneLieuNaissance(enfant, LARGEURS_LIEU_APRES_DATE).champs,
              ],
            },
          ],
        },
      ],
      individu: (individu, tab) =>
        tab === 'enfant'
          ? [
              {
                titre: 'INFORMATIONS DE NAISSANCE',
                // L'enfant n'a ni « Né le » (il est dans le bloc ÉVÉNEMENT),
                // ni lieu de naissance, ni profession.
                lignes: [{ champs: champsEtatCivil(individu, [4, 6, 6]) }],
              },
            ]
          : [
              {
                titre: 'INFORMATIONS DE NAISSANCE',
                lignes: [
                  {
                    champs: [
                      {
                        libelle: 'Né le',
                        valeur: formatDateSeule(individu?.naissanceDate),
                        computer: 4,
                        tablet: 8,
                        mobile: 16,
                      },
                      ...champsEtatCivil(individu),
                    ],
                  },
                  ligneLieuNaissance(individu),
                ],
              },
              sectionProfession(individu),
            ],
    },
    modales: {
      registreActe: ModalRegistreActe,
      evenement: ModalEvenementNaissance,
      individus: { enfant: ModalEnfant },
      parent: ModalParent,
    },
  },

  reconnaissance: {
    url: URL_NUMERISATION_RECONNAISSANCE,
    urlIndividus: URL_ACTE_INDIVIDUS,
    getTitre: ({ acte }) =>
      `Acte de reconnaissance de ${acte?.nomClassementEnfant ?? ''}`,
    titrePanelEvenement: 'INFORMATIONS SUR LA RECONNAISSANCE',
    cleMentionActe: 'identifiantActeReconnaissance',
    onglets: ongletsAvecRoles('reconnaissance'),
    ListeSidebar: ActeReconnaissanceListeContainer,
    blocs: {
      registreActe: ({ acte }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Registre',
                  valeur: formatRegistreActe(acte),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  // `libelleTypeActe` vaut toujours la constante
                  // « Reconnaissance » : l'information utile est le SOUS-type.
                  libelle: "Sous-type d'acte",
                  valeur: acte?.libelleSousTypeActe,
                  computer: 3,
                  tablet: 8,
                  mobile: 16,
                },
                ...champsActe(acte),
              ],
            },
          ],
        },
      ],
      // La reconnaissance décrit l'évènement depuis l'ACTE, avec un lieu
      // IMBRIQUÉ (`lieuReconnaissance`, nul si aucun lieu n'a été saisi) et un
      // champ « Auteur » supplémentaire.
      evenement: ({ acte }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Reconnu le',
                  valeur: formatDateHeure(
                    acte?.dateReconnaissance,
                    heureReconnaissance(acte),
                    minuteReconnaissance(acte),
                  ),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Auteur de la reconnaissance',
                  valeur: acte?.libelleAuteurReconnaissance,
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Département',
                  valeur: formatDepartement(
                    acte?.lieuReconnaissance?.codeDepartement,
                    acte?.lieuReconnaissance?.libelleDepartement,
                  ),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Commune (Pays)',
                  valeur: formatCommunePays(
                    acte?.lieuReconnaissance?.codePostal,
                    acte?.lieuReconnaissance?.localite,
                    acte?.lieuReconnaissance?.libellePays,
                  ),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
              ],
            },
          ],
        },
      ],
      individu: (individu, tab) =>
        tab === 'enfant'
          ? [
              {
                titre: 'INFORMATIONS DE NAISSANCE',
                lignes: [
                  {
                    champs: [
                      {
                        // Le bloc ÉVÉNEMENT portant la reconnaissance, la date
                        // de naissance de l'enfant (avec son heure) vit ici.
                        // Toujours pas de lieu de naissance pour l'enfant.
                        libelle: 'Né le',
                        valeur: formatDateHeure(
                          individu?.naissanceDate,
                          individu?.naissanceHeure,
                          individu?.naissanceMinute,
                        ),
                        computer: 4,
                        tablet: 8,
                        mobile: 16,
                      },
                      ...champsEtatCivil(individu),
                    ],
                  },
                ],
              },
            ]
          : [
              {
                titre: 'INFORMATIONS DE NAISSANCE',
                lignes: [
                  {
                    champs: [
                      {
                        libelle: 'Né le',
                        valeur: formatDateSeule(individu?.naissanceDate),
                        computer: 4,
                        tablet: 8,
                        mobile: 16,
                      },
                      ...champsEtatCivil(individu),
                    ],
                  },
                  ligneLieuNaissance(individu),
                ],
              },
              // Pas de section « DIVERS » : la profession est
              // réservée à la naissance (`ec_actr` n'a pas cette colonne).
            ],
    },
    modales: {
      registreActe: ModalRegistreActe,
      // Bloc propre à la reconnaissance : l'évènement est porté par l'acte (et
      // non par l'enfant), avec un champ « Auteur » dont le changement peut
      // rendre sans objet le bloc de l'autre parent.
      evenement: ModalEvenementReconnaissance,
      // Les individus passent par `urlIndividus`, endpoint générique.
      individus: { enfant: ModalEnfant },
      parent: ModalParent,
    },
    /** Props supplémentaires de `ModalRegistreActe` propres à ce type d'acte. */
    evenementPorteParLActe: true,
    propsModaleParent: {
      // La profession n'est ni affichée ni saisie en reconnaissance.
      showProfession: false,
      // La civilité découle du rôle (père / mère), comme dans l'assistant.
      disabledSexe: true,
      libellesParents: libellesParentsAvecArticle('reconnaissance'),
    },
    propsModaleRegistre: {
      urlSousTypesActe: URL_TYPES_RECONNAISSANCE,
      typeRegistre: TYPE_REGISTRE_BY_TYPE_ACTE.reconnaissance,
      champLibelleSousType: 'libelleSousTypeActe' as const,
    },
  },
  deces: {
    url: URL_NUMERISATION_DECES,
    urlIndividus: URL_ACTE_INDIVIDUS,
    getTitre: ({ acte }) =>
      `Acte de décès de ${acte?.nomClassementDefunt ?? ''}`,
    titrePanelEvenement: 'INFORMATIONS SUR LE DÉCÈS',
    cleMentionActe: 'identifiantActeDeces',
    onglets: ONGLETS_DECES,
    ListeSidebar: ActeDecesListeContainer,
    /**
     * La maquette titre le panneau des individus par le RÔLE de l'onglet actif
     * (« INFORMATIONS SUR LE DÉFUNT »), là où la naissance et la reconnaissance
     * gardent « INDIVIDUS ».
     */
    getTitrePanelIndividus: (tab) =>
      ({
        defunt: 'INFORMATIONS SUR LE DÉFUNT',
        parent1: 'INFORMATIONS SUR LE PARENT 1',
        parent2: 'INFORMATIONS SUR LE PARENT 2',
        declarant: 'INFORMATIONS SUR LE DÉCLARANT',
      })[tab as string] ?? 'INDIVIDUS',
    blocs: {
      registreActe: ({ acte }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Registre',
                  valeur: formatRegistreActe(acte),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  // Contrairement à la reconnaissance, `ad_a_typl` porte bien le
                  // SOUS-type : « Décès », « Transcription de décès »,
                  // « Enfant sans vie »… Il n'y a pas de colonne séparée.
                  libelle: "Sous-type d'acte",
                  valeur: acte?.libelleTypeActe,
                  computer: 3,
                  tablet: 8,
                  mobile: 16,
                },
                ...champsActe(acte),
              ],
            },
          ],
        },
      ],
      /*
       * ⚠️ La situation matrimoniale n'est PAS ici, bien que `ec_actd` la porte :
       * elle vit dans l'onglet du défunt. C'est la répartition par DOMAINE qui
       * commande l'écran, pas celle des tables.
       *
       * ⚠️ Le lieu se lit sur le DÉFUNT et non sur l'acte, d'où un descripteur
       * qui lit les deux.
       */
      evenement: ({ acte, defunt }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Décédé le',
                  valeur: formatDateHeure(
                    acte?.dateDeces,
                    acte?.heureDeces,
                    acte?.minuteDeces,
                  ),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                ...ligneLieuPrefixe(defunt, 'deces', LARGEURS_LIEU_APRES_DATE)
                  .champs,
              ],
            },
          ],
        },
      ],
      individu: (individu, tab, dossier) => {
        if (tab === 'declarant') {
          // Le déclarant n'a ni date ni lieu de naissance mais un ÂGE : parler
          // d'« informations de naissance » serait faux, d'où un autre titre.
          return [
            {
              titre: 'IDENTITÉ',
              lignes: [
                {
                  champs: [
                    ...champsEtatCivil(individu),
                    {
                      libelle: 'Âge',
                      valeur: individu?.age,
                      computer: 4,
                      tablet: 8,
                      mobile: 16,
                    },
                  ],
                },
              ],
            },
            // Un seul crayon pour tout l'onglet du déclarant : ses trois
            // sections tiennent dans une modale courte, aucun trait donc.
            {
              titre: 'DOMICILIATION',
              separateur: false,
              lignes: [ligneLieuPrefixe(individu, 'domicile', [3, 9])],
            },
            sectionProfession(individu, 'DIVERS', false),
          ];
        }

        if (tab === 'defunt') {
          return [
            {
              titre: 'INFORMATIONS DE NAISSANCE',
              lignes: [
                {
                  champs: [
                    {
                      libelle: 'Né le',
                      valeur: formatDateHeure(
                        individu?.naissanceDate,
                        individu?.naissanceHeure,
                        individu?.naissanceMinute,
                      ),
                      computer: 4,
                      tablet: 8,
                      mobile: 16,
                    },
                    ...champsEtatCivil(individu),
                  ],
                },
                ligneLieuNaissance(individu),
              ],
            },
            {
              titre: 'DOMICILIATION',
              /*
               * Pas de trait : cette section partage son crayon avec
               * « INFORMATIONS DE NAISSANCE » — les deux forment le premier
               * groupe, édité par `ModalDefunt`.
               */
              separateur: false,
              lignes: [ligneLieuPrefixe(individu, 'domicile')],
            },
            /*
             * « DIVERS » ouvre le SECOND groupe : trait au-dessus, crayon
             * propre, et `ModalDefuntDivers` qui l'édite avec la section
             * matrimoniale. Deux modales courtes plutôt qu'une à dérouler.
             */
            sectionProfession(individu, 'DIVERS', true, ModalDefuntDivers),
            sectionEtatMatrimonial(dossier?.acte),
          ];
        }

        // Père / mère du défunt : même forme qu'en naissance, profession
        // incluse — la maquette de l'assistant la demande sous « Divers ».
        return [
          {
            titre: 'INFORMATIONS DE NAISSANCE',
            lignes: [
              {
                champs: [
                  {
                    libelle: 'Né le',
                    valeur: formatDateSeule(individu?.naissanceDate),
                    computer: 4,
                    tablet: 8,
                    mobile: 16,
                  },
                  ...champsEtatCivil(individu),
                ],
              },
              ligneLieuNaissance(individu),
            ],
          },
          // Même groupe que la naissance : `ModalParent` édite les deux.
          sectionProfession(individu, 'DIVERS', false),
        ];
      },
    },
    modales: {
      registreActe: ModalRegistreActe,
      // Date et heure du décès (portées par l'acte) ET lieu du décès (porté par
      // le défunt) : deux endpoints, cf. le JSDoc de la modale.
      evenement: ModalEvenementDeces,
      /*
       * Modale du PREMIER groupe de chaque onglet. Les groupes suivants
       * déclarent la leur sur leur première section
       * (`SectionChamps.modale`) — c'est le cas de « DIVERS » chez le défunt.
       */
      individus: { defunt: ModalDefunt, declarant: ModalDeclarant },
      parent: ModalParent,
    },
    evenementPorteParLActe: true,
    /*
     * Aucune prop à surcharger pour la modale de parent : les parents d'un acte
     * de décès se saisissent exactement comme ceux d'une naissance —
     * « PARENT 1 / PARENT 2 », civilité LIBRE, profession affichée. C'est la
     * reconnaissance qui fait exception, pas le décès.
     */
    propsModaleRegistre: {
      urlSousTypesActe: URL_SOUS_TYPES_ACTE_DECES,
      typeRegistre: TYPE_REGISTRE_BY_TYPE_ACTE.deces,
      // `ad_a_typl` porte le sous-type : c'est bien ce champ qu'on réécrit.
      champLibelleSousType: 'libelleTypeActe' as const,
    },
  },
  mariage: {
    url: URL_NUMERISATION_MARIAGE,
    urlIndividus: URL_ACTE_INDIVIDUS,
    getTitre: ({ acte }) =>
      `Acte de mariage de ${acte?.conjoint1 ?? ''} et ${acte?.conjoint2 ?? ''}`,
    titrePanelEvenement: 'INFORMATIONS SUR LE MARIAGE',
    // Quatrième panneau, propre au mariage : cf. `sectionContratMariage`.
    titrePanelComplement: 'CONTRAT DE MARIAGE',
    cleIndividuComplement: 'notaire',
    cleMentionActe: 'identifiantActeMariage',
    onglets: ONGLETS_MARIAGE,
    ListeSidebar: ActeMariageListeContainer,
    getTitrePanelIndividus: () => 'CONJOINTS',
    blocs: {
      registreActe: ({ acte }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Registre',
                  valeur: formatRegistreActe(acte),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  // `am_a_typl` porte bien le SOUS-type : « Mariage », « Projet
                  // de mariage », « Reprise de vie commune ».
                  libelle: "Sous-type d'acte",
                  valeur: acte?.libelleTypeActe,
                  computer: 3,
                  tablet: 8,
                  mobile: 16,
                },
                ...champsActe(acte),
              ],
            },
          ],
        },
        /*
         * L'officier d'état civil : un SOUS-BLOC titré du panneau de l'acte, et
         * trois champs distincts — c'est ainsi que la maquette le présente.
         *
         * Les colonnes structurées, pas la phrase rendue de `am_a_sign`, que la
         * numérisation n'écrit pas.
         */
        {
          titre: "Officier d'état civil",
          lignes: [
            {
              champs: [
                {
                  libelle: 'Nom de famille',
                  valeur: acte?.acteSignataireNomFamille,
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Prénom',
                  valeur: acte?.acteSignatairePrenoms,
                  computer: 3,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Grade signataire',
                  valeur: acte?.acteSignataireQualite,
                  computer: 9,
                  tablet: 16,
                  mobile: 16,
                },
              ],
            },
          ],
        },
      ],
      /*
       * Le lieu de la célébration est une ligne `ec_acte_adresse` imbriquée dans
       * l'acte (`lieuMariage`), distincte de celle de l'acte lui-même.
       *
       * Le CONTRAT a son propre panneau (`blocs.complement`) : la maquette lui
       * donne un crayon séparé, et le notaire y figure.
       */
      evenement: ({ acte }) => [
        {
          lignes: [
            {
              champs: [
                {
                  libelle: 'Marié le',
                  valeur: formatDateHeure(
                    acte?.dateMariage,
                    acte?.heureMariage,
                    acte?.minuteMariage,
                  ),
                  computer: 4,
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Département',
                  valeur: formatDepartement(
                    acte?.lieuMariage?.codeDepartement,
                    acte?.lieuMariage?.libelleDepartement,
                  ),
                  computer: LARGEURS_LIEU_APRES_DATE[0],
                  tablet: 8,
                  mobile: 16,
                },
                {
                  libelle: 'Commune (Pays)',
                  valeur: formatCommunePays(
                    acte?.lieuMariage?.codePostal,
                    acte?.lieuMariage?.localite,
                    acte?.lieuMariage?.libellePays,
                  ),
                  computer: LARGEURS_LIEU_APRES_DATE[1],
                  tablet: 16,
                  mobile: 16,
                },
              ],
            },
          ],
        },
      ],
      complement: ({ acte, notaire }) => sectionContratMariage(acte, notaire),
      individu: (individu, tab, dossier) => {
        if (tab === 'parentsConjoint1' || tab === 'parentsConjoint2') {
          const numeroConjoint = tab === 'parentsConjoint1' ? 1 : 2;
          const suffixe = `Conjoint${numeroConjoint}`;

          return [
            // EN TÊTE de l'onglet : la situation du couple se lit avant les deux
            // personnes qu'elle qualifie, et non intercalée entre elles.
            sectionEtatMatrimonialParents(dossier?.acte, numeroConjoint),
            ...sectionsParentMariage(
              dossier?.[`parent1${suffixe}` as keyof DossierActe],
              `parent1${suffixe}`,
              1,
            ),
            ...sectionsParentMariage(
              dossier?.[`parent2${suffixe}` as keyof DossierActe],
              `parent2${suffixe}`,
              2,
            ),
          ];
        }

        // Conjoint 1 ou 2 : même forme que le défunt d'un décès, deux groupes.
        const numeroConjoint = tab === 'conjoint1' ? 1 : 2;

        return [
          {
            titre: 'INFORMATIONS DE NAISSANCE',
            lignes: [
              {
                champs: [
                  {
                    libelle: 'Né le',
                    valeur: formatDateSeule(individu?.naissanceDate),
                    computer: 4,
                    tablet: 8,
                    mobile: 16,
                  },
                  ...champsEtatCivil(individu),
                ],
              },
              ligneLieuNaissance(individu),
            ],
          },
          {
            titre: 'DOMICILIATION',
            // Même groupe que la naissance : `ModalConjoint{n}` édite les deux.
            separateur: false,
            lignes: [ligneLieuPrefixe(individu, 'domicile')],
          },
          // « DIVERS » ouvre le SECOND groupe, édité avec la section antérieure.
          sectionProfession(
            individu,
            'DIVERS',
            true,
            numeroConjoint === 1 ? ModalConjoint1Divers : ModalConjoint2Divers,
          ),
          sectionEtatAnterieur(dossier?.acte, numeroConjoint),
        ];
      },
    },
    modales: {
      registreActe: ModalRegistreActe,
      // Date et heure (acte) et lieu (`ec_acte_adresse`) : deux endpoints, cf.
      // le JSDoc de la modale.
      evenement: ModalEvenementMariage,
      // Contrat (acte) et notaire (individu) : deux endpoints également.
      complement: ModalContratMariage,
      individus: { conjoint1: ModalConjoint1, conjoint2: ModalConjoint2 },
      parent: ModalParent,
    },
    evenementPorteParLActe: true,
    propsModaleRegistre: {
      urlSousTypesActe: URL_SOUS_TYPES_ACTE_MARIAGE,
      typeRegistre: TYPE_REGISTRE_MARIAGE,
      // `am_a_typl` porte le sous-type : c'est bien ce champ qu'on réécrit.
      champLibelleSousType: 'libelleTypeActe' as const,
      // Le crayon de ce panneau doit atteindre l'officier, qu'il affiche.
      champsOfficier: true,
    },
  },
};

export default FICHE_CONFIG;
